import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  BadgeAlt as Badge,
  Calendar,
  Input,
  Separator,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cargomatic/ui';
import { format, parse, isValid } from 'date-fns';
import { CalendarIcon, PlusCircle, MoveDown } from 'lucide-react';
import { debounce } from 'lodash';
import { cn } from 'utils/ui/classes';
import { sluggify } from 'utils/localization/utils';

const types = {
  EQUALS: 'equals',
  AFTER: 'after',
  BEFORE: 'before',
  BETWEEN: 'range',
};

export function DataTableDateFilter({ column, title, className }) {
  const today = new Date();
  const { t } = useTranslation();
  const value = column?.getFilterValue();
  const [type, setType] = useState(value?.type ?? types.BEFORE);
  const [dateFrom, setDateFrom] = useState(value?.dateFrom ?? today);
  const [dateTo, setDateTo] = useState(value?.dateTo ?? today);
  const [dateOn, setDateOn] = useState(value?.dateOn ?? today);
  const [month, setMonth] = useState(today);
  const [open, setOpen] = useState(false);

  function populateDateFields() {
    setType(value?.type ?? types.BEFORE);
    setDateFrom(value?.dateFrom ?? today);
    setDateTo(value?.dateTo ?? today);
    setDateOn(value?.dateOn ?? today);
  }

  useEffect(() => {
    populateDateFields();
    if (!value) {
      setType(types.BEFORE);
    }
  }, [value]);

  function handleOpenChange(open) {
    setOpen(open);
    if (!open && !value) {
      populateDateFields();
    }
  }
  function isApplyButtonDisabled() {
    if (
      (type === types.BEFORE && dateTo) ||
      (type === types.AFTER && dateFrom) ||
      (type === types.EQUALS && dateOn)
    ) {
      return false;
    }
    return !(type && dateFrom && dateTo);
  }

  function isDateFilterSet() {
    return value?.dateFrom || value?.dateTo || value?.dateOn;
  }

  function handleApply() {
    const filter = {};
    if (type === types.EQUALS) filter.dateOn = dateOn;
    if (type === types.BEFORE || type === types.BETWEEN) filter.dateTo = dateTo; // why does this line pass the linter when the following doesn't?
    if (type === types.AFTER || type === types.BETWEEN)
      filter.dateFrom = dateFrom;

    filter.type = type;

    column?.setFilterValue(filter);
    setOpen(false);
  }

  function handleSelectDateRange(selected) {
    const { dateFrom, dateTo } = selected || {};
    setDateFrom(dateFrom);
    setDateTo(dateTo);
  }

  function handleReset() {
    setDateFrom();
    setDateTo();
    setDateOn();
    column.setFilterValue(undefined);
  }

  function formatLongDateString(date) {
    return format(date, 'PPP');
  }

  function formatDateFilter() {
    if (type === types.AFTER && value.dateFrom) {
      return `${t(sluggify('After'))} ${formatLongDateString(value.dateFrom)}`;
    }

    if (type === types.BEFORE && value.dateTo) {
      return `${t(sluggify('Before'))} ${formatLongDateString(value.dateTo)}`;
    }

    if (type === types.EQUALS && value.dateOn) {
      return `${t(sluggify('Equals'))} ${formatLongDateString(value.dateOn)}`;
    }

    if (value.dateFrom && value.dateTo) {
      const dateRangeString = [
        formatLongDateString(value.dateFrom),
        formatLongDateString(value.dateTo),
      ].join(' - ');
      return `${t(sluggify('Between'))} ${dateRangeString}`;
    }
  }

  return (
    <Popover modal={false} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-dashed flex items-center"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          {title}
          {isDateFilterSet() && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge className="rounded-sm px-1">{formatDateFilter()}</Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('overflow-y-auto space-y-2', className)}
        align="start"
      >
        <Select id="type" onValueChange={setType} value={type} className="">
          <SelectTrigger className="">
            <SelectValue placeholder={t(sluggify('Select'))} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Type</SelectLabel>
              {Object.entries(types).map(([slug, value]) => (
                <SelectItem key={slug} value={value}>
                  {t(slug)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {type === types.EQUALS && (
          <SingleDayPicker
            selected={dateOn}
            onSelect={setDateOn}
            month={month}
            onMonthChange={setMonth}
          />
        )}
        {type === types.AFTER && (
          <SingleDayPicker
            selected={dateFrom}
            onSelect={setDateFrom}
            month={month}
            onMonthChange={setMonth}
          />
        )}
        {type === types.BEFORE && (
          <SingleDayPicker
            selected={dateTo}
            onSelect={setDateTo}
            month={month}
            onMonthChange={setMonth}
          />
        )}
        {type === types.BETWEEN && (
          <DayRangePicker
            selected={{ dateFrom, dateTo }}
            onSelect={handleSelectDateRange}
            month={month}
            onMonthChange={setMonth}
          />
        )}
        <hr className="" />

        <div className="sm:flex sm:items-center sm:space-x-2 gap-2 sm:justify-end">
          <Button
            variant=""
            onClick={handleApply}
            className="w-full sm:w-auto"
            disabled={isApplyButtonDisabled()}
          >
            {t(sluggify('Apply'))}
          </Button>

          <Button
            variant="secondary"
            onClick={handleReset}
            className="w-full sm:w-auto"
          >
            {t(sluggify('Clear'))}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SingleDayPicker = ({
  selected,
  onSelect,
  month: inputMonth,
  onMonthChange,
}) => {
  const [date, setDate] = useState(selected);
  const [month, setMonth] = useState(inputMonth || new Date());

  const handleSelect = (date) => {
    setDate(date);
    onSelect(date);
  };

  const handleMonthChange = (date) => {
    setMonth(date);
    onMonthChange(date);
  };

  return (
    <>
      <DateInput
        date={date}
        onChange={handleSelect}
        onMonthChange={handleMonthChange}
      />
      <Calendar
        month={month}
        onMonthChange={handleMonthChange}
        mode="single"
        selected={selected}
        onSelect={handleSelect}
        initialFocus
      />
    </>
  );
};

const DayRangePicker = ({
  selected,
  onSelect,
  month: inputMonth,
  onMonthChange,
}) => {
  const { dateFrom, dateTo } = selected;
  const [from, setFrom] = useState(dateFrom);
  const [to, setTo] = useState(dateTo);
  const [month, setMonth] = useState(inputMonth || new Date());

  function handleSelect(selected, _, modifiers = {}) {
    const { from: dateFrom, to: dateTo } = selected || {};
    // handling cases where the user re-selects a date
    if (modifiers.selected && !modifiers.range_middle) {
      if (modifiers.range_start) {
        setFrom();
      }
      if (modifiers.range_end) {
        setTo();
      }
    } else {
      setFrom(dateFrom);
      setTo(dateTo);
      onSelect({ dateFrom, dateTo });
    }
  }

  const handleMonthChange = (date) => {
    setMonth(date);
    onMonthChange(date);
  };

  useEffect(() => {
    if (from !== dateFrom || to !== dateTo) handleSelect({ from, to });
    // handling cases where 'from' and 'to' need to swap
    if (from && to && from > to) {
      setFrom(to);
      setTo(from);
    }
  }, [to, from]);

  return (
    <>
      <DateInput
        date={from}
        onChange={setFrom}
        onMonthChange={handleMonthChange}
        placeholder="Enter an after date"
      />
      <div className="grid justify-items-center">
        <MoveDown
          className={cn('h-4', (!from || !to) && 'text-muted-foreground')}
        />
      </div>
      <DateInput
        date={to}
        onChange={setTo}
        onMonthChange={handleMonthChange}
        placeholder="Enter a before date"
      />
      <Calendar
        month={month}
        onMonthChange={handleMonthChange}
        mode="range"
        selected={{ to, from }}
        onSelect={handleSelect}
        initialFocus
      />
    </>
  );
};

const DateInput = ({
  date,
  onChange,
  onMonthChange,
  placeholder = 'Enter a date',
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState(date ? format(date, 'P') : '');

  useEffect(() => {
    setInput(date ? format(date, 'P') : ''); // Update input when date changes
  }, [date]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Debounce the input changes with a delay of 300ms
  const debouncedInputChange = debounce((value) => {
    let parsedDate = parse(value, 'P', new Date());
    parsedDate = isValid(parsedDate)
      ? parsedDate
      : parse(value, 'PPP', new Date());

    if (isValid(parsedDate) && parsedDate >= new Date(0)) {
      onChange(parsedDate);
      onMonthChange?.(parsedDate);
    } else if (value === '') {
      onChange(undefined);
    }
  }, 100);

  useEffect(() => {
    debouncedInputChange(input);

    return () => {
      debouncedInputChange.cancel();
    };
  }, [input]);

  return (
    <div className="relative">
      <CalendarIcon
        className={cn(
          'absolute left-2.5 top-1/3 h-1/3 w-auto',
          !date && 'text-muted-foreground',
        )}
      />
      <Input
        variant="outline"
        className={cn(
          'p-1.5 pl-7 border rounded w-full font-normal',
          !date && 'text-muted-foreground',
        )}
        placeholder={t(sluggify(placeholder))}
        value={input}
        onChange={handleInputChange}
      />
    </div>
  );
};
